import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { StepConfig } from "./flowConfig.types";
import { DEFAULT_FLOW_CONDITIONS } from "./flowConditions";
import type { FlowConditionContext } from "./flowConditions";

interface ActiveFlowContextValue {
  steps: StepConfig[];
  flowContext: FlowConditionContext;
  selectedDbTypeId: string | null;
  setActiveFlow: (dbTypeId: string, steps: StepConfig[]) => void;
  patchFlowContext: (patch: Partial<FlowConditionContext>) => void;
  resetFlow: () => void;
}

const ActiveFlowContext = createContext<ActiveFlowContextValue>({
  steps: [],
  flowContext: DEFAULT_FLOW_CONDITIONS,
  selectedDbTypeId: null,
  setActiveFlow: () => {},
  patchFlowContext: () => {},
  resetFlow: () => {},
});

export const ActiveFlowProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [steps, setSteps] = useState<StepConfig[]>([]);
  const [flowContext, setFlowContext] = useState<FlowConditionContext>(
    DEFAULT_FLOW_CONDITIONS,
  );
  const [selectedDbTypeId, setSelectedDbTypeId] = useState<string | null>(null);

  const setActiveFlow = useCallback(
    (dbTypeId: string, newSteps: StepConfig[]) => {
      setSelectedDbTypeId(dbTypeId);
      setSteps(newSteps);
      setFlowContext(DEFAULT_FLOW_CONDITIONS);
    },
    [],
  );

  const patchFlowContext = useCallback(
    (patch: Partial<FlowConditionContext>) => {
      setFlowContext((prev) => ({ ...prev, ...patch }));
    },
    [],
  );

  const resetFlow = useCallback(() => {
    setSteps([]);
    setSelectedDbTypeId(null);
    setFlowContext(DEFAULT_FLOW_CONDITIONS);
  }, []);

  const value = useMemo(
    () => ({
      steps,
      flowContext,
      selectedDbTypeId,
      setActiveFlow,
      patchFlowContext,
      resetFlow,
    }),
    [
      steps,
      flowContext,
      selectedDbTypeId,
      setActiveFlow,
      patchFlowContext,
      resetFlow,
    ],
  );

  return (
    <ActiveFlowContext.Provider value={value}>
      {children}
    </ActiveFlowContext.Provider>
  );
};

export const useActiveFlow = (): ActiveFlowContextValue =>
  useContext(ActiveFlowContext);

export default ActiveFlowContext;
